/**
 * FERP Portal - Instance Cleanup Log Page
 *
 * Displays the status and audit trail of the CleanStaleInstances processor (DRR-182).
 * The processor scans Valkey reservation keys for stale instances (TTL < 1h),
 * cancels Running instances with 0 completed tasks, and deletes reservation keys.
 *
 * NOTE: No dedicated GraphQL query exists yet. The processor writes audit events
 * to DDB (Event primitive). This page shows known processor behavior and would
 * query audit events once the backend resolver is implemented.
 *
 * DRY: This monitoring pattern applies to any outbox processor that performs
 * background cleanup. Other processors can add their own audit log pages
 * following this template.
 *
 * CONSTRAINTS:
 * - Mantine v8.x only
 * - Dumb Frontend
 * - Admin view layer only
 */

import {
  Alert,
  Badge,
  Card,
  Container,
  Group,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconBrush,
  IconInfoCircle,
  IconShieldCheck,
  IconTrash,
} from '@tabler/icons-react';

export function InstanceCleanupLogPage() {
  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        <Group justify="space-between">
          <div>
            <Title order={2}>Instance Cleanup</Title>
            <Text c="dimmed" size="sm">
              DRR-182 — CleanStaleInstances processor audit trail
            </Text>
          </div>
          <Badge variant="light" color="teal" size="lg">
            Automated
          </Badge>
        </Group>

        <Alert icon={<IconInfoCircle size={16} />} color="blue" title="Background Processor">
          The CleanStaleInstances processor runs automatically via the outbox pipeline.
          It scans Valkey reservation keys (<Text span ff="monospace" size="sm">rsv:&#123;tenant&#125;:*</Text>)
          with TTL under 1 hour and cancels Running instances that have zero completed tasks.
          Instances with real progress are left untouched. A dedicated audit query is planned.
        </Alert>

        <Card withBorder p="md">
          <Group gap="sm" mb="md">
            <ThemeIcon variant="light" color="teal">
              <IconBrush size={18} />
            </ThemeIcon>
            <Text fw={600}>How Cleanup Works</Text>
          </Group>
          <Stack gap="xs">
            <Text size="sm">
              1. Outbox processor fires <Text span ff="monospace" size="sm">CleanStaleInstances</Text> action
            </Text>
            <Text size="sm">
              2. Scans Valkey <Text span ff="monospace" size="sm">rsv:&#123;tenant&#125;:*</Text> keys with TTL {'<'} 1 hour
            </Text>
            <Text size="sm">
              3. For each stale reservation: check DDB instance → if Running + 0 completed tasks → cancel
            </Text>
            <Text size="sm">
              4. Delete Valkey reservation key (frees the slot for new instances)
            </Text>
            <Text size="sm">
              5. Instances with completed tasks are left untouched (real work in progress)
            </Text>
          </Stack>
        </Card>

        <Card withBorder p="md">
          <Group gap="sm" mb="sm">
            <ThemeIcon variant="light" color="green" size="sm">
              <IconShieldCheck size={14} />
            </ThemeIcon>
            <Text fw={600}>Safety Guards</Text>
          </Group>
          <Stack gap="xs">
            <Group gap="xs">
              <Badge variant="outline" size="xs" color="green">Safe</Badge>
              <Text size="sm">Only cancels instances with 0 completed tasks</Text>
            </Group>
            <Group gap="xs">
              <Badge variant="outline" size="xs" color="green">Safe</Badge>
              <Text size="sm">Never deletes DDB records — sets status to Cancelled</Text>
            </Group>
            <Group gap="xs">
              <Badge variant="outline" size="xs" color="green">Safe</Badge>
              <Text size="sm">Audit event written for every cancellation (T1 tier, indefinite)</Text>
            </Group>
            <Group gap="xs">
              <Badge variant="outline" size="xs" color="red">Guard</Badge>
              <Text size="sm">Instances with any task completion are NEVER touched</Text>
            </Group>
          </Stack>
        </Card>

        <Card withBorder p="md">
          <Group gap="sm" mb="sm">
            <ThemeIcon variant="light" color="orange" size="sm">
              <IconTrash size={14} />
            </ThemeIcon>
            <Text fw={600}>What Gets Cleaned</Text>
          </Group>
          <Text size="sm" c="dimmed">
            Stale instances are typically created by abandoned workflow launches: user opens
            WorkflowLaunchModal, draft reservation is created in Valkey, but the user navigates
            away without committing. After the reservation TTL drops below 1 hour, the processor
            cleans up the zombie instance and frees the reservation slot.
          </Text>
        </Card>

        <Text size="xs" c="dimmed">
          Cleanup events are queryable via the Event primitive (type: instance_cleanup).
          A dedicated audit query will replace this static page when implemented.
        </Text>
      </Stack>
    </Container>
  );
}

export default InstanceCleanupLogPage;
